import { useCallback, useEffect } from 'react';

import { toast } from 'sonner';

import {
    Channel,
    EventType,
    registerNativeBridgeReceiver,
    useAppBridge,
    useAppContext,
    useCurrentMenu,
    useEventBusEmit,
    useEventBusOn,
    useMenuRouter,
    useQuery,
    useRestContext,
    useUserContext,
    useWebviewBridge,
    useWebviewContext,
    WebviewVersion,
} from 'scbk-services';
import { usePopupStore } from 'scbk-shared-module';

/**
 *
 * @returns
 */
const NativeBridgeSubscriber: React.FC = () => {
    const { channel } = useAppContext().getContext();
    const { doNCloseBizWebView, doNGetCurrentWebviewStatus } = useWebviewBridge();
    const {
        doNGetWebviewStorage,
        doNSetWebviewStorage,
        doNRemoveWebviewStorage,
        doNClearWebviewStorage,
    } = useAppBridge();
    const { query } = useQuery();
    const webviewType = query['webviewType'];
    const { currentMenuId } = useCurrentMenu();
    const webviewContext = useWebviewContext();
    const emit = useEventBusEmit();
    //const router = useRouter();
    const { goMenu } = useMenuRouter();

    const { resetDynamicContext } = useRestContext();
    const { resetCommonContext, resetAdditionalContext } = useUserContext();
    const { reset: resetPopup } = usePopupStore();

    /**
     * 유저컨텍스트 초기화
     */
    const clearUserContext = useCallback(() => {
        resetCommonContext();
        resetAdditionalContext();
        resetDynamicContext();
    }, [resetCommonContext, resetAdditionalContext, resetDynamicContext]);

    /**
     * 네이티브 브릿지 콜백 수신 처리 등록
     */
    useEffect(() => {
        registerNativeBridgeReceiver();
    }, []);

    //*************************************
    //웹뷰 open/close 이벤트 처리
    //*************************************
    useEventBusOn({
        eventType: EventType.WebviewOpen,
        listener: async () => {
            console.log('# EventType.WebviewOpen');

            //현재 웹뷰 상태 inactive 로 업데이트
            webviewContext.inactiveWebview();
        },
    });

    useEventBusOn({
        eventType: EventType.WebviewClose,
        listener: async () => {
            console.log('# EventType.WebviewClose');

            //현재 웹뷰 상태 inactive 로 업데이트
            webviewContext.inactiveWebview();
            doNCloseBizWebView({ type: (webviewType ?? 'v4') as WebviewVersion });
        },
    });

    //*************************************
    //스토리지 동기화 처리
    //*************************************

    //로컬스토리지 setItem
    useEventBusOn({
        eventType: EventType.SetItemLocalStorage,
        listener: async (args) => {
            console.log('### EventType.SetItemLocalStorage', args);
            const arr = args as [string, string];
            const key = arr[0] as string;
            const value = arr[1] as string;
            await doNSetWebviewStorage(key, value);
        },
    });

    //로컬스토리지 removeItem
    useEventBusOn({
        eventType: EventType.RemoveItemLocalStorage,
        listener: async (args) => {
            console.log('### EventType.SetItemLocalStorage', args);
            const arr = args as [string];
            await doNRemoveWebviewStorage(arr[0]);
        },
    });

    //로컬스토리지 clear
    useEventBusOn({
        eventType: EventType.ClearLocalStorage,
        listener: async () => {
            console.log('### EventType.ClearLocalStorage');
            await doNClearWebviewStorage();
        },
    });

    const getNavigationContext = useCallback(async () => {
        try {
            const value = await doNGetWebviewStorage('navigationContext');
            if (!value) {
                return;
            }
            return JSON.parse(value);
        } catch (e) {
            console.error(e);
            return;
        }
    }, []);

    //세션 관련 네이티브 -> 웹 이벤트 처리
    useEffect(() => {
        const sessionEventHandler = (event: Event) => {
            try {
                const customEvent = event as CustomEvent;
                const data = JSON.parse(customEvent.detail);

                console.log('# session event data=', data);

                const { type } = data;
                switch (type) {
                    case '0': //로그아웃 버튼 클릭
                        emit(EventType.LogoutAction);
                        break;
                    case '1': //로그인 연장 버튼 클릭
                        emit(EventType.ExtendSession);
                        break;
                    case '2': //로그인 세션타이머 만료
                        emit(EventType.ExpireSession);
                        break;
                }
            } catch (e) {
                console.error(e);
            }
        };

        window.addEventListener('native:session:timer', sessionEventHandler);

        return () => {
            window.removeEventListener('native:session:timer', sessionEventHandler);
        };
    }, []);

    //사용자 로그아웃 처리 관련
    useEffect(() => {
        if (channel !== Channel.MB) {
            return;
        }

        const logoutEventHandler = async () => {
            //유저컨텍스트 초기화
            clearUserContext();

            // if (webviewType !== 'main') {
            //     return;
            // }

            console.log('## native:logout currentMenuId=', currentMenuId);

            // 결함번호 154626 네비바 상태 삭제
            sessionStorage.removeItem('savedNavBarStatus');

            // 결함번호 154626 네비바 Hide 처리
            emit(EventType.HideNavbar);

            //*************************************
            //결함번호 135808 대응 토스트 메시지 닫기
            //*************************************
            toast.dismiss();

            // 로그아웃시에 알럿 없애 버리기
            resetPopup();

            if (webviewType === 'main') {
                //일반/편한뱅킹 홈이면.. PRE-LOGIN 으로 이동
                //탭이동이 아닌.. 웹뷰내에서 메뉴이동으로 처리해야한다.
                if (
                    currentMenuId === 'KLDHOM000' || // Home
                    currentMenuId === 'KLDCMF000' // 편한뱅킹 Home
                ) {
                    goMenu('LGNADV000', { historyOptions: { clearHistory: true } });
                } else {
                    //goMenu('COMPRE000', { historyOptions: { clearHistory: true } });
                }
            } else {
                const { isTop } = await doNGetCurrentWebviewStatus();
                if (isTop) {
                    return;
                }

                //업무웹뷰를 닫는다.
                doNCloseBizWebView({ type: (webviewType ?? 'v4') as WebviewVersion });
            }
        };

        window.addEventListener('native:logout', logoutEventHandler);
        return () => {
            //
            window.removeEventListener('native:logout', logoutEventHandler);
        };
    }, [channel, webviewType, currentMenuId]);

    //*************************************
    // 웹뷰간 이벤트 브로드캐스트 이벤트 처리
    //*************************************
    useEffect(() => {
        if (channel !== Channel.MB) {
            return;
        }

        const handleBroadcastEvent = (e: Event) => {
            try {
                const customEvent = e as CustomEvent;
                const detail = JSON.parse(customEvent.detail);

                console.log('# session event data=', detail);

                const { event, data } = detail;
                console.log(`# handleBroadcastEvent event ${event}, data=${data}`);
            } catch (e) {
                console.error(e);
            }
        };

        window.addEventListener('webview:broadcast', handleBroadcastEvent);
        return () => {
            window.removeEventListener('webview:broadcast', handleBroadcastEvent);
        };
    }, [channel]);

    useEffect(() => {
        if (channel !== Channel.MB) {
            return;
        }

        const handleWebviewResume = async () => {
            const navigationContext = await getNavigationContext();

            window.dispatchEvent(
                new CustomEvent('webview:resume', {
                    detail: {
                        context: navigationContext,
                    },
                }),
            );

            if (navigationContext) {
                doNRemoveWebviewStorage('navigationContext');
            }
        };

        window.addEventListener('native:webviewResume', handleWebviewResume);
        return () => {
            window.removeEventListener('native:webviewResume', handleWebviewResume);
        };
    }, [channel]);

    useEffect(() => {
        if (channel !== Channel.MB) {
            return;
        }

        const handleWebviewPause = async () => {
            window.dispatchEvent(new CustomEvent('webview:pause'));
        };

        window.addEventListener('native:webviewPause', handleWebviewPause);
        return () => {
            window.removeEventListener('native:webviewPause', handleWebviewPause);
        };
    }, [channel]);

    return null;
};

export { NativeBridgeSubscriber };
